"use client";

import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { Button } from "@/components/ui/button";
import { BaseModal } from "@/components/ui/base-modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { selectCurrentUserEmail } from "@/store/modules/users/users.selectors";
import type { BrandEmailTemplatePreviewType } from "./brand-email-template-preview-button";

interface BrandEmailTemplateSendTestButtonProps {
  brandId: string;
  templateType: BrandEmailTemplatePreviewType;
  disabled?: boolean;
  variant?: "ghost" | "outline";
  size?: "sm" | "default";
}

export function BrandEmailTemplateSendTestButton({
  brandId,
  templateType,
  disabled,
  variant = "ghost",
  size = "sm",
}: BrandEmailTemplateSendTestButtonProps) {
  const currentUserEmail = useSelector(selectCurrentUserEmail) ?? "";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toEmail, setToEmail] = useState(currentUserEmail);

  useEffect(() => {
    if (dialogOpen) {
      setToEmail(currentUserEmail);
    }
  }, [dialogOpen, currentUserEmail]);

  const { doAction: sendTestEmail, isProcessing } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (to: string) =>
        sdk.brands.sendTestEmail(brandId, { to, templateType }),
      onSuccess: () => {
        toast.success(`Test email sent to ${toEmail.trim()}`);
        setDialogOpen(false);
      },
      failureMessage: "Failed to send test email",
    }),
    [brandId, templateType, toEmail]
  );

  const canSend = Boolean(toEmail.trim()) && !isProcessing;

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={() => setDialogOpen(true)}
      >
        <Send className="mr-2 h-4 w-4" />
        Send Test Email
      </Button>

      <BaseModal
        title="Send Test Email"
        description="A rendered test email with sample booking data will be sent so you can verify how it looks in your email client."
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        okText="Send Test Email"
        okButtonIcon={<Send className="h-4 w-4" />}
        okButtonLoadingIndicator={isProcessing}
        okButtonDisabled={!canSend}
        onOk={() => {
          const to = toEmail.trim();
          if (!to) {
            toast.error("Enter a recipient email address");
            return;
          }
          sendTestEmail(to);
        }}
        dialogContentClassName="max-w-md"
      >
        <div className="space-y-2">
          <Label htmlFor="send-test-email-to">Recipient email</Label>
          <Input
            id="send-test-email-to"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            disabled={isProcessing}
          />
          <p className="text-sm text-muted-foreground">
            Uses your currently saved brand template configuration and sample
            booking data — not a real customer booking.
          </p>
        </div>
      </BaseModal>
    </>
  );
}
